<template>
    <div class="flex items-center justify-center h-screen px-6 bg-color-blue-gradient">
        <div class="w-full max-w-sm p-6 bg-white rounded-md shadow-md">
            <div class="flex items-center justify-center mb-10">
                <span class="text-2xl font-semibold text-gray-700">
                    <img class="logo large-logo" src="@/assets/img/ceLogo2.svg" alt="logo image" />
                </span>
            </div>
            
            <h1 class="title mb-2">Code de validation Google</h1>
            <p class="sub-title mb-4">Veuillez insérer votre code disponible dans l'application (Google authenticator, Authy, Microsoft authenticator, Duo mobile ou autre) sur votre téléphone</p>
            
            <form @submit.prevent="submitCode">
                <div class="input-group">
                    <input
                        v-for="(input, index) in code"
                        :key="index"
                        type="text"
                        v-model="code[index]"
                        maxlength="1"
                        :id="`input${index}`"
                        @input="focusNextInput($event, index)"
                        pattern="[0-9]*"
                        title="Seuls les chiffres sont autorisés"
                        oninvalid="setCustomValidity('Seuls les chiffres sont autorisés')"
                        oninput="setCustomValidity('')"
                        @keydown="focusPreviousInput($event, index)"
                    />
                </div>
                <p v-if="error" class="error">{{ error }}</p>
                <div class="mb-4"></div>
                <div class="flex">
                    <button class="btn btn-primary connect" type="submit" :disabled="!isCodeComplete">Se connecter</button>
                    <button class="btn btn-danger cancel-login" type="button" @click="logout">Annuler</button>
                </div>
            </form>
        </div>
    </div>
</template>

<script lang="ts" setup>
import {ref, computed} from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth/authStore';

const router = useRouter();
const authStore = useAuthStore();

const code = ref(["", "", "", "", "", ""]);
const error = ref<string | null>(null);

const isCodeComplete = computed(() => {
    return code.value.every(digit => digit !== "");
});

const refs = {};
for (let i = 0; i < 6; i++) {
    refs[`input${i}`] = ref(null);
}

const focusNextInput = (event: InputEvent, inputIndex: number) => {
    if (event.target?.value.length === 1 && inputIndex < code.value.length) {
        const nextInput = `input${inputIndex + 1}`;
        const nextInputElement = document.getElementById(`${nextInput}`);
        if (nextInputElement) {
            nextInputElement.focus();
        }
    }
};

function focusPreviousInput(event: KeyboardEvent, inputIndex: number) {
    if ((event.key === 'Backspace' || event.key === 'Delete') && event.target?.value === '') {
        const previousInput = `input${inputIndex - 1}`;
        const previousInputElement = document.getElementById(`${previousInput}`);
        if (previousInputElement) {
            previousInputElement.focus();
        }
    }
}

const submitCode = async () => {
    const autCode = parseInt(code.value.join(''), 10);
    
    if (Math.abs(autCode).toString().length === 6) {
        error.value = null;
        
        try {
            const storedEmail = localStorage.getItem('userEmail') ?? '';
            const codeValidation = await authStore.verifyGoogle2faCode(autCode, storedEmail);
            if (
                codeValidation.data &&
                codeValidation.data.token &&
                codeValidation.data.refresh_token
            ){
                await router.push({ name: 'dashboard' });
            }
        } catch (errorResponse) {
            console.log(errorResponse);
            error.value = 'Le code est invalide.';
        }
    } else {
        error.value = "Le code doit comporter 6 chiffres.";
    }
}

function logout() {
    authStore.logout();
}

</script>

<style scoped>
.error {
    color: red;
    text-align: center;
}
.input-group {
    justify-content: center;
}
input{
    width: 30px;
    padding: 10px;
    margin-right: 15px;
    background-color: #FFFFFF;
    color: #333333;
    border-top: 0;
    border-bottom: 0;
    border-bottom: 0;
    border-bottom: 1px solid #999;
    border-radius: 0 !important;
}
button.connect {
    margin-right: 6px;
    background-color: rgb(1 51 131);
    width: 50%;
}
button.connect:disabled {
    background-color: #CCCCCC;
    border: #CCCCCC;
    cursor: not-allowed;
}
button.cancel-login {
    background-color: rgba(174, 185, 208, 0.7);
    border: rgba(194, 205, 228, 1);
    width: 40%;
}
button.connect, button.cancel-login {
    font-weight: normal;
    text-transform: capitalize;
    color: #fff;
    cursor: pointer;
}
button.cancel-login:hover {
    background-color: rgba(174, 185, 208, 1);
}
.bg-color-blue-gradient {
    background: linear-gradient(112.26deg, #275CF1 -28.65%, #0B1C48 91.24%)
}
.title, .sub-title {
    color: rgb(1 51 131);
}
.title {
    font-size: 20px;
    font-weight: bold;
    text-align: center;
}
.large-logo {
    width: 200px;
    height: auto;
}
</style>